import React, { Fragment, useMemo, useState } from 'react';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Legend,
  Customized,
  ReferenceLine,
} from 'recharts';

// Duplicate of issues list (could be centralized later)
const ISSUES = [
  { key: 'Homelessness', 'Sep 2023': 94, 'Jul 2025': 92 },
  { key: 'Open air drug use', 'Sep 2023': 84, 'Jul 2025': 81 },
  { key: 'Fentanyl dealing', 'Sep 2023': 81, 'Jul 2025': 81 },
  // { key: 'Crime and lack of safety', 'Sep 2023': 79 },
  { key: 'Cost of housing & rent', 'Sep 2023': 79, 'Jul 2025': 80 },
  // { key: 'Cost of living', 'Jul 2025': 83 },
  // { key: 'Untreated mental health issues', 'Jul 2025': 82 },
  // { key: 'Drug and substance abuse', 'Jul 2025': 75 },
  // { key: 'Homeless encampments', 'Jul 2025': 70 },
  // { key: 'Ineffectiveness in SF City government', 'Sep 2023': 72 },
  { key: 'Corruption in SF City government', 'Sep 2023': 50, 'Jul 2025': 50 },
  { key: 'The downtown SF economy', 'Sep 2023': 68, 'Jul 2025': 63 },
  {
    key: 'The difficulty of opening or running a small business',
    'Sep 2023': 63,
    'Jul 2025': 62,
  },
  // { key: 'Difficulty of building or renovating homes', 'Jul 2025': 60 },
  // { key: 'Declining quality of life', 'Jul 2025': 56 },
  // { key: 'Property crime like burglaries and car break-ins', 'Jul 2025': 55 },
  { key: 'The quality of public education', 'Sep 2023': 57, 'Jul 2025': 53 },
  //{ key: 'Declining tax revenues', 'Jul 2025': 51 },
  //{ key: 'A shortage of police officers', 'Jul 2025': 49 },
  //{ key: 'Too little growth & development', 'Jul 2025': 49 },
  { key: 'Jobs & Unemployment', 'Sep 2023': 39, 'Jul 2025': 48 },
  // { key: 'The condition of streets', 'Sep 2023': 46 },
  // { key: 'Parking availability', 'Sep 2023': 36 },
  { key: 'Traffic congestion', 'Sep 2023': 27, 'Jul 2025': 26 },
  { key: 'Lack of access to public transit', 'Sep 2023': 26, 'Jul 2025': 30 },
  { key: 'Too much growth & development', 'Sep 2023': 17, 'Jul 2025': 13 },
  { key: 'Violent crime', 'Sep 2023': 60, 'Jul 2025': 39 },
  // { key: 'Influence of tech on City government', 'Jul 2025': 35 },
  // { key: 'Safety on public transit', 'Jul 2025': 46 },
];

const WAVES = ['Sep 2023', 'Jul 2025'];
const COLORS = {
  'Sep 2023': '#d94b42', // earlier wave
  'Jul 2025': '#145c9e', // latest wave
};

// Simple word wrap for axis labels
const wrapLabel = (text, maxChars = 32) => {
  if (!text) return [];
  const words = text.split(' ');
  const lines = [];
  let current = '';
  words.forEach((w) => {
    if ((current + w).length <= maxChars) {
      current = current ? current + ' ' + w : w;
    } else {
      if (current) lines.push(current);
      // If single word longer than maxChars, hard break it
      while (w.length > maxChars) {
        lines.push(w.slice(0, maxChars));
        w = w.slice(maxChars);
      }
      current = w;
    }
  });
  if (current) lines.push(current);
  return lines;
};

// Base rows preserve original list order; sorting applied dynamically
const baseRows = ISSUES.map((i) => ({
  issue: i.key,
  prev: i['Sep 2023'] ?? null,
  latest: i['Jul 2025'] ?? null,
}));
const CHART_ROW_HEIGHT = 32; // increased to fit larger circles

// Minimalist renderer: small dots + connecting line (when both waves present)
const DumbbellRenderer = ({ xAxisMap, yAxisMap, data, lineIssueSet }) => {
  const xScale = Object.values(xAxisMap)[0].scale;
  const yScale = Object.values(yAxisMap)[0].scale;
  const RADIUS = 6;
  return (
    <g className="dumbbell-layer">
      {data.map((d) => {
        const bandTop = yScale(d.issue);
        if (bandTop == null) return null;
        const yCenter = bandTop + (yScale.bandwidth ? yScale.bandwidth() / 2 : 0);
        const prevX = d.prev != null ? xScale(d.prev) : null;
        const latestX = d.latest != null ? xScale(d.latest) : null;
        const maxX = [prevX, latestX]
          .filter((v) => v != null)
          .sort((a, b) => a - b)
          .pop();
        const positions = [prevX, latestX].filter((v) => v != null).sort((a, b) => a - b);
        const minX = positions[0];
        const anchorRight = maxX; // rightmost dot
        return (
          <g key={d.issue}>
            {prevX != null && latestX != null && lineIssueSet.has(d.issue) && (
              <line
                x1={prevX}
                x2={latestX}
                y1={yCenter}
                y2={yCenter}
                stroke="#6b737b"
                strokeWidth={3}
                strokeLinecap="round"
                opacity={0.85}
              />
            )}
            {prevX != null && (
              <g>
                <circle
                  cx={prevX}
                  cy={yCenter}
                  r={RADIUS}
                  fill={COLORS['Sep 2023']}
                  stroke="none"
                  strokeWidth={0}
                />
              </g>
            )}
            {latestX != null && (
              <g>
                <circle
                  cx={latestX}
                  cy={yCenter}
                  r={RADIUS}
                  fill={COLORS['Jul 2025']}
                  stroke="none"
                  strokeWidth={0}
                />
                {/* Latest percentage label */}
                <text
                  x={latestX + 3}
                  y={yCenter + RADIUS + 11}
                  fontSize={10}
                  fontWeight={300}
                  textAnchor="middle"
                  fill="#1f2937"
                >
                  {d.latest}%
                </text>
              </g>
            )}
            {maxX != null &&
              d.prev != null &&
              d.latest != null &&
              (() => {
                const delta = d.latest - d.prev;
                if (delta === 0) return false;
                const positive = delta > 0;
                const x = positive ? anchorRight + RADIUS + 6 : minX - RADIUS - 6;
                const ta = positive ? 'start' : 'end';
                return (
                  <text
                    x={x}
                    y={yCenter + 5}
                    fontSize={12}
                    fontWeight={700}
                    textAnchor={ta}
                    fill={positive ? '#7f1d1d' : '#065f46'}
                    stroke="#fff"
                    strokeWidth={3}
                    paintOrder="stroke"
                  >
                    {(delta > 0 ? '+' : '') + delta}
                  </text>
                );
              })()}
          </g>
        );
      })}
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-gray-300 rounded px-2 py-1 text-xs">
      <div className="font-semibold mb-1" style={{ maxWidth: 240 }}>
        {row.issue}
      </div>
      {WAVES.map((w) => (
        <div key={w} className="flex justify-between gap-4">
          <span>{w}:</span>
          <span>
            {row[w === 'Sep 2023' ? 'prev' : 'latest'] != null
              ? `${row[w === 'Sep 2023' ? 'prev' : 'latest']}%`
              : '—'}
          </span>
        </div>
      ))}
      {row.prev != null &&
        row.latest != null &&
        (() => {
          const delta = row.latest - row.prev;
          return delta !== 0;
        })() && (
          <div className="mt-1">
            Change:{' '}
            <span
              className={(() => {
                const delta = row.latest - row.prev;
                return 'font-semibold ' + (delta > 0 ? 'text-red-800' : 'text-green-700');
              })()}
            >
              {(() => {
                const delta = row.latest - row.prev;
                return (delta > 0 ? '+' : '') + delta;
              })()}
            </span>
          </div>
        )}
    </div>
  );
};

export const IssuesDumbbellChart = ({ title = 'All Tracked Issues – Change Between Waves' }) => {
  const [sortKey, setSortKey] = useState('latest'); // 'latest' | 'prev' | 'delta'

  const data = useMemo(() => {
    const rows = [...baseRows];
    const cmp = (a, b) => {
      if (sortKey === 'latest') {
        const av = a.latest ?? -1;
        const bv = b.latest ?? -1;
        if (bv !== av) return bv - av;
        // tie break by prev then name
        const ap = a.prev ?? -1;
        const bp = b.prev ?? -1;
        if (bp !== ap) return bp - ap;
      } else if (sortKey === 'prev') {
        const av = a.prev ?? -1;
        const bv = b.prev ?? -1;
        if (bv !== av) return bv - av;
        const al = a.latest ?? -1;
        const bl = b.latest ?? -1;
        if (bl !== al) return bl - al;
      } else if (sortKey === 'delta') {
        const ad = a.prev != null && a.latest != null ? a.latest - a.prev : null;
        const bd = b.prev != null && b.latest != null ? b.latest - b.prev : null;
        const aAbs = ad == null ? -1 : Math.abs(ad);
        const bAbs = bd == null ? -1 : Math.abs(bd);
        if (bAbs !== aAbs) return bAbs - aAbs; // larger magnitude first
        if (bd !== ad) return (bd ?? -1) - (ad ?? -1); // then actual delta
      }
      return a.issue.localeCompare(b.issue);
    };
    rows.sort(cmp);
    return rows;
  }, [sortKey]);

  const chartHeight = Math.max(300, data.length * CHART_ROW_HEIGHT + 80);

  const allValues = [];
  data.forEach((d) => {
    if (typeof d.prev === 'number') allValues.push(d.prev);
    if (typeof d.latest === 'number') allValues.push(d.latest);
  });
  let xMin = 0;
  let xMax = 100;
  if (allValues.length) {
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    xMin = Math.max(0, Math.floor((min - 2) / 5) * 5);
    xMax = Math.min(100, Math.ceil((max + 2) / 5) * 5);
  }

  // Lines for all issues with both waves
  const lineIssueSet = new Set(
    data.filter((d) => d.prev != null && d.latest != null).map((d) => d.issue)
  );

  return (
    <Fragment>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-2">
        <p className="text-left text-gray-700 text-sm font-semibold uppercase">{title}</p>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Sort:</span>
          {[
            { key: 'latest', label: 'Jul 2025' },
            { key: 'prev', label: 'Sep 2023' },
            { key: 'delta', label: 'Δ change' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortKey(opt.key)}
              className={`px-2 py-1 rounded border text-[11px] font-medium transition-colors ${
                sortKey === opt.key
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
        >
          <CartesianGrid horizontal={true} vertical={false} strokeDasharray="3 3" />
          <XAxis
            type="number"
            domain={[xMin, xMax]}
            tickLine={false}
            axisLine={false}
            ticks={[0, 25, 50, 75, 100].filter((v) => v >= xMin && v <= xMax)}
            tick={{ fontSize: 12 }}
            label={{
              value: '% Extremely/Very Serious',
              position: 'bottom',
              offset: 0,
              fontSize: 13,
            }}
          />
          <YAxis
            dataKey="issue"
            type="category"
            width={250}
            interval={0}
            tickLine={false}
            axisLine={false}
            tick={({ y, payload, x }) => {
              const lines = wrapLabel(payload.value);
              const lineHeight = 16; // px for larger font
              const startY = y - ((lines.length - 1) * lineHeight) / 2 + 6; // center align
              return (
                <text fontSize={13} textAnchor="end" fill="#374151">
                  {lines.map((line, i) => (
                    <tspan key={i} x={x - 8} y={startY + i * lineHeight}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }} />
          <Legend
            verticalAlign="top"
            align="right"
            wrapperStyle={{ top: 0, width: '100%', display: 'flex', justifyContent: 'flex-end' }}
            payload={[]}
            content={() => (
              <div className="flex items-center gap-4 pr-2 pt-1 text-sm select-none justify-end">
                {WAVES.map((w) => (
                  <div key={w} className="flex items-center gap-1">
                    <span
                      className="inline-block rounded-full"
                      style={{ background: COLORS[w], width: 14, height: 14 }}
                    />
                    <span className="text-[13px] tracking-wide text-gray-700 font-medium">{w}</span>
                  </div>
                ))}
                <div className="flex items-center pl-2 border-l border-gray-300">
                  <img
                    src={require('../../images/logo-clean.svg').default}
                    alt="GrowSF"
                    style={{ height: 18, width: 'auto', display: 'block' }}
                  />
                </div>
              </div>
            )}
          />
          {/* Minor reference lines at 25%, 50%, 75% */}
          {[25, 50, 75]
            .filter((v) => v >= xMin && v <= xMax)
            .map((v) => (
              <ReferenceLine key={v} x={v} stroke="#d1d5db" strokeDasharray="2 4" strokeWidth={1} />
            ))}
          <Customized component={<DumbbellRenderer data={data} lineIssueSet={lineIssueSet} />} />
        </ComposedChart>
      </ResponsiveContainer>
    </Fragment>
  );
};

export default IssuesDumbbellChart;
